import React, {
  Children,
  Fragment,
  HTMLProps,
  ReactElement,
  ReactNode,
  RefObject,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useControlledState } from "@react-stately/utils";
import { mergeProps } from "@react-aria/utils";
import { PlayerStoreContext } from "@common/player/player-context";
import {
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
} from "react-router-dom";
import { DialogContext } from "@ui/overlays/dialog/dialog-context";
import { createEventHandler } from "@ui/utils/dom/create-event-handler";
import { VirtualElement } from "@floating-ui/react-dom";
import { rootEl } from "@ui/root-el";
import { playerOverlayState } from "@app/web-player/state/player-overlay-store";
import {
  StackScreenContent,
  logStackNavigator,
  stackNavigatorState,
  useStackNavigatorStore,
} from "./stack-navigator-store";
import { StackSurface } from "./stack-outlet";

interface StackTriggerProps {
  children: [ReactElement, StackScreenContent] | ReactNode;
  title?: string;
  stackedHeaderRight?: React.ReactNode;
  stackedBodyPadding?: boolean;
  overlayClassName?: string;
  closePlayerOverlayOnOpen?: boolean;
  onClose?: (value?: any) => void;
  isDismissable?: boolean;
  isOpen?: boolean;
  defaultIsOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  triggerRef?: RefObject<HTMLElement> | RefObject<VirtualElement>;
  triggerOnContextMenu?: boolean;
  handleTriggerClick?: boolean;
  moveFocusToDialog?: boolean;
  returnFocusToTrigger?: boolean;
}

export function StackTrigger(props: StackTriggerProps) {
  const {
    children,
    title,
    stackedHeaderRight,
    stackedBodyPadding,
    overlayClassName,
    closePlayerOverlayOnOpen = false,
    onClose,
    isDismissable = true,
    isOpen: controlledIsOpen,
    defaultIsOpen,
    onOpenChange,
    triggerRef,
    triggerOnContextMenu = false,
    handleTriggerClick: shouldHandleTriggerClick = true,
    returnFocusToTrigger = true,
  } = props;
  const [isOpen, setIsOpen] = useControlledState(
    controlledIsOpen,
    defaultIsOpen,
    onOpenChange,
  );
  const playerStore = useContext(PlayerStoreContext);
  const parentDialog = useContext(DialogContext);
  const routeContext = useContext(UNSAFE_RouteContext);
  const locationContext = useContext(UNSAFE_LocationContext);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const activeKeyRef = useRef<string | null>(null);
  const latestIsOpenRef = useRef(isOpen);
  const latestOnCloseRef = useRef(onClose);
  const latestTriggerElementRef = useRef<HTMLElement | null>(null);
  const preserveScreenOnUnmountRef = useRef(false);

  latestIsOpenRef.current = isOpen;
  latestOnCloseRef.current = onClose;

  useEffect(() => {
    mountedRef.current = true;
    logStackNavigator("trigger.mount", {title});
    return () => {
      mountedRef.current = false;
      const activeKey = activeKeyRef.current;
      logStackNavigator("trigger.unmount", {title, activeKey});
      activeKeyRef.current = null;
      if (activeKey && !preserveScreenOnUnmountRef.current) {
        stackNavigatorState.completeClose(activeKey);
      }
    };
  }, []);

  const { stackTrigger, stackContent } = useMemo(
    () => extractChildren(children),
    [children],
  );

  const handleScreenClosed = useCallback(
    (value?: any) => {
      logStackNavigator("trigger.onClosed", {
        title,
        activeKey: activeKeyRef.current,
        value,
      });
      preserveScreenOnUnmountRef.current = false;
      activeKeyRef.current = null;
      if (mountedRef.current) {
        setActiveKey(null);
        if (latestIsOpenRef.current) {
          setIsOpen(false);
        }
      }
      latestOnCloseRef.current?.(value);
    },
    [setIsOpen, title],
  );

  const getScreenInput = useCallback(
    () => ({
      title,
      stackedHeaderRight,
      stackedBodyPadding,
      className: overlayClassName,
      isDismissable,
      returnFocusToTrigger,
      renderInOutlet: preserveScreenOnUnmountRef.current,
      playerStore: preserveScreenOnUnmountRef.current ? playerStore : undefined,
      routeContext: preserveScreenOnUnmountRef.current ? routeContext : undefined,
      locationContext: preserveScreenOnUnmountRef.current
        ? locationContext
        : undefined,
      triggerElement:
        latestTriggerElementRef.current ||
        (triggerRef?.current instanceof HTMLElement ? triggerRef.current : null),
      content: stackContent,
      onClosed: handleScreenClosed,
    }),
    [
      handleScreenClosed,
      isDismissable,
      overlayClassName,
      returnFocusToTrigger,
      stackContent,
      stackedBodyPadding,
      stackedHeaderRight,
      title,
      routeContext,
      locationContext,
      playerStore,
      triggerRef,
    ],
  );

  const activeScreenState = useStackNavigatorStore(
    useCallback(state => {
      if (!activeKey) {
        return null;
      }

      const stackIndex = state.entries.findIndex(item => item.key === activeKey);
      if (stackIndex === -1) {
        return null;
      }

      return {
        entry: state.entries[stackIndex],
        stackIndex,
        previousSurfaceKey:
          stackIndex > 0 ? state.entries[stackIndex - 1].key : null,
        canGoBack: stackIndex > 0,
        isTopScreen: stackIndex === state.entries.length - 1,
      };
    }, [activeKey]),
  );
  const completeClose = useStackNavigatorStore(state => state.completeClose);

  useEffect(() => {
    if (!isOpen) {
      if (activeKeyRef.current) {
        logStackNavigator("trigger.effect.requestClose", {
          title,
          key: activeKeyRef.current,
        });
        stackNavigatorState.requestClose(activeKeyRef.current);
      }
      return;
    }

    if (!activeKeyRef.current) {
      const nextActiveKey = stackNavigatorState.push(getScreenInput());
      logStackNavigator("trigger.effect.push", {
        title,
        key: nextActiveKey,
      });
      activeKeyRef.current = nextActiveKey;
      setActiveKey(nextActiveKey);
      return;
    }

    logStackNavigator("trigger.effect.update", {
      title,
      key: activeKeyRef.current,
    });
    stackNavigatorState.update(activeKeyRef.current, getScreenInput());
  }, [
    getScreenInput,
    isOpen,
  ]);

  const open = useCallback(
    (triggerElement?: HTMLElement | null) => {
      const currentTriggerElement =
        triggerElement ||
        (triggerRef?.current instanceof HTMLElement ? triggerRef.current : null);

      if (currentTriggerElement) {
        latestTriggerElementRef.current = currentTriggerElement;
      }

      const overlayElement = document.querySelector(
        ".player-overlay-surface, .player-overlay-bg",
      );
      const shouldClosePlayerOverlay =
        closePlayerOverlayOnOpen &&
        !!overlayElement &&
        !!currentTriggerElement &&
        overlayElement.contains(currentTriggerElement);
      const shouldCloseParentDialog =
        !shouldClosePlayerOverlay && parentDialog?.type === "tray";
      const shouldPreserveScreenOnUnmount =
        shouldClosePlayerOverlay || shouldCloseParentDialog;

      logStackNavigator("trigger.open", {
        title,
        currentKey: activeKeyRef.current,
        isOpen,
        hasTriggerElement: !!currentTriggerElement,
        shouldClosePlayerOverlay,
        shouldCloseParentDialog,
      });
      if (activeKeyRef.current || isOpen) {
        logStackNavigator("trigger.open.skipped", {
          title,
          currentKey: activeKeyRef.current,
          isOpen,
        });
        return;
      }

      if (shouldPreserveScreenOnUnmount) {
        preserveScreenOnUnmountRef.current = true;
        const nextActiveKey = stackNavigatorState.push(getScreenInput());
        activeKeyRef.current = nextActiveKey;
        setActiveKey(nextActiveKey);
        setIsOpen(true);
        if (shouldClosePlayerOverlay) {
          playerOverlayState.close();
        } else {
          parentDialog?.close();
        }
        return;
      }

      preserveScreenOnUnmountRef.current = false;
      setIsOpen(true);
    },
    [
      closePlayerOverlayOnOpen,
      getScreenInput,
      isOpen,
      parentDialog,
      setIsOpen,
      title,
      triggerRef,
    ],
  );

  useEffect(() => {
    logStackNavigator("trigger.activeScreenState", {
      title,
      activeKey,
      hasState: !!activeScreenState,
      stackIndex: activeScreenState?.stackIndex,
      isTopScreen: activeScreenState?.isTopScreen,
      previousSurfaceKey: activeScreenState?.previousSurfaceKey,
      rootEl: !!rootEl,
    });
  }, [activeKey, activeScreenState, title]);

  const handleTriggerContextMenu: HTMLProps<HTMLElement> = {
    onContextMenu: createEventHandler((event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      open(event.currentTarget as HTMLElement);
    }),
  };

  const triggerClickProps: HTMLProps<HTMLElement> = {
    onClick: createEventHandler((event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      open(event.currentTarget as HTMLElement);
    }),
  };

  return (
    <Fragment>
      {stackTrigger &&
        React.cloneElement(
          stackTrigger,
          mergeProps(
            {
              ...(triggerOnContextMenu ? handleTriggerContextMenu : {}),
              ...(!triggerOnContextMenu && shouldHandleTriggerClick
                ? triggerClickProps
                : {}),
            },
            stackTrigger.props,
          ),
        )}
      {activeScreenState &&
        !activeScreenState.entry.renderInOutlet &&
        rootEl &&
        createPortal(
          <StackSurface
            entry={activeScreenState.entry}
            stackIndex={activeScreenState.stackIndex}
            isTopScreen={activeScreenState.isTopScreen}
            canGoBack={activeScreenState.canGoBack}
            previousSurfaceKey={activeScreenState.previousSurfaceKey}
            onCloseComplete={completeClose}
          />,
          rootEl,
        )}
    </Fragment>
  );
}

function extractChildren(rawChildren: StackTriggerProps["children"]) {
  const children = Array.isArray(rawChildren)
    ? rawChildren
    : Children.toArray(rawChildren);

  if (children.length === 2) {
    return {
      stackTrigger: children[0] as ReactElement,
      stackContent: children[1] as StackScreenContent,
    };
  }

  return {
    stackTrigger: null,
    stackContent: children[0] as StackScreenContent,
  };
}
